import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import {
  format,
  differenceInMinutes,
  subDays,
  startOfDay,
  endOfDay,
  parseISO,
  eachDayOfInterval,
} from "date-fns";
import MDSpinner from "react-md-spinner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import myConfig from "../../configs/config";
import DateTime from "../../components/DateTime";
import ExportButton from "../../components/Export_button";
import BackButton from "../../components/BackButton";
import PolygonMap from "../../components/Polygon_map";

import "./AttendanceHistory.css";

function AttendanceHistory(props) {
  const params = useParams();
  const farmId = params.farm_id;

  const [workers, setWorkers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const [mapTitle, setMapTitle] = useState("");

  // Fetch workers
  const fetchWorkers = useCallback(async () => {
    try {
      const WORKER_PROFILE_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/?farm_id=${farmId}&is_active=true`;

      const response = await axios({
        baseURL: WORKER_PROFILE_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      setWorkers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching workers:", error);
      toast.error(props.t("Falha ao carregar trabalhadores"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [farmId, props]);

  // Fetch attendance history
  const fetchAttendanceHistory = useCallback(async () => {
    try {
      setLoading(true);
      const fromDate = format(startOfDay(startDate), "yyyy-MM-dd");
      const toDate = format(endOfDay(endDate), "yyyy-MM-dd");

      let ATTENDANCE_HISTORY_URL = `${myConfig.BE_URL}/api/attendance/records/?farm_id=${farmId}&is_active=true&from_date=${fromDate}&to_date=${toDate}`;

      if (selectedWorkers.length > 0 && !selectedWorkers.includes("all")) {
        ATTENDANCE_HISTORY_URL += `&worker_ids=${selectedWorkers.join(",")}`;
      }

      const response = await axios({
        baseURL: ATTENDANCE_HISTORY_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      // Transform new API format to flat records array
      let allRecords = [];
      if (data.success && data.workers) {
        data.workers.forEach((workerData) => {
          const workerProfile = workerData.worker_profile;
          workerData.records.forEach((record) => {
            allRecords.push({
              ...record,
              worker_profile: workerProfile.id,
              worker_name: workerProfile.operator_name,
              latitude: record.location_latitude,
              longitude: record.location_longitude,
            });
          });
        });
      }

      setAttendanceRecords(allRecords);

      // Process data for charts
      processChartData(allRecords);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching attendance history:", error);
      toast.error(props.t("Falha ao carregar histórico"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  }, [startDate, endDate, selectedWorkers, farmId, props, workers]);

  // Process chart data
  const processChartData = useCallback(
    (records) => {
      // Group records by worker and date
      const workerDailyHours = {};
      const dailyAttendance = {};

      // Get all days in range
      const days = eachDayOfInterval({ start: startDate, end: endDate });

      days.forEach((day) => {
        const dayKey = format(day, "yyyy-MM-dd");
        dailyAttendance[dayKey] = {
          date: format(day, "dd/MM"),
          workers: 0,
          totalHours: 0,
        };
      });

      records.forEach((record) => {
        const workerId = record.worker_profile;
        const recordDate = new Date(record.timestamp);
        const date = format(recordDate, "yyyy-MM-dd");

        if (!workerDailyHours[workerId]) {
          workerDailyHours[workerId] = {};
        }

        if (!workerDailyHours[workerId][date]) {
          workerDailyHours[workerId][date] = {
            clockIn: null,
            clockOut: null,
          };
        }

        if (record.action === "clock_in") {
          workerDailyHours[workerId][date].clockIn = record.timestamp;
        } else if (record.action === "clock_out") {
          workerDailyHours[workerId][date].clockOut = record.timestamp;
        }
      });

      // Calculate daily hours for each worker
      const workerChartData = [];
      const workersSet = new Set();

      Object.keys(workerDailyHours).forEach((workerId) => {
        const worker = workers.find((w) => w.id === parseInt(workerId));
        if (!worker) return;

        const workerName = `${worker.first_name} ${worker.last_name}`;
        workersSet.add(workerId);

        Object.keys(workerDailyHours[workerId]).forEach((date) => {
          const { clockIn, clockOut } = workerDailyHours[workerId][date];

          if (clockIn && clockOut) {
            const clockInDate = new Date(clockIn);
            const clockOutDate = new Date(clockOut);
            const hours = differenceInMinutes(clockOutDate, clockInDate) / 60;

            workerChartData.push({
              date: format(parseISO(date), "dd/MM"),
              worker: workerName,
              hours: parseFloat(hours.toFixed(2)),
            });

            // Update daily attendance
            if (dailyAttendance[date]) {
              dailyAttendance[date].totalHours += hours;
            }
          }
        });
      });

      // Update workers count per day
      Object.keys(dailyAttendance).forEach((date) => {
        const dayRecords = records.filter((r) => {
          const recordDate = new Date(r.timestamp);
          return format(recordDate, "yyyy-MM-dd") === date;
        });
        const uniqueWorkers = new Set(dayRecords.map((r) => r.worker_profile))
          .size;
        dailyAttendance[date].workers = uniqueWorkers;
      });

      setChartData(workerChartData);
      setDailyData(Object.values(dailyAttendance));
    },
    [workers, startDate, endDate]
  );

  // Initial load
  useEffect(() => {
    document.title = "Agriconnected - Histórico de Ponto";
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    if (workers.length > 0) {
      fetchAttendanceHistory();
    }
  }, [workers.length, fetchAttendanceHistory]);

  // Get grouped records for table display
  const getGroupedRecords = () => {
    const grouped = {};

    attendanceRecords.forEach((record) => {
      const workerId = record.worker_profile;
      const recordDate = new Date(record.timestamp);
      const date = format(recordDate, "yyyy-MM-dd");
      const key = `${workerId}-${date}`;

      if (!grouped[key]) {
        grouped[key] = {
          workerId,
          date,
          clockIn: null,
          clockOut: null,
          clockInLocation: null,
          clockOutLocation: null,
        };
      }

      if (record.action === "clock_in") {
        grouped[key].clockIn = record.timestamp;
        grouped[key].clockInLocation = {
          lat: record.latitude,
          lng: record.longitude,
        };
      } else if (record.action === "clock_out") {
        grouped[key].clockOut = record.timestamp;
        grouped[key].clockOutLocation = {
          lat: record.latitude,
          lng: record.longitude,
        };
      }
    });

    return Object.values(grouped).sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  };

  const groupedRecords = getGroupedRecords();

  // Calculate total hours
  const calculateTotalHours = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return "-";

    const clockInDate = new Date(clockIn);
    const clockOutDate = new Date(clockOut);
    const duration = differenceInMinutes(clockOutDate, clockInDate);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    return `${hours}h ${minutes}m`;
  };

  const handleWorkerSelection = (e) => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map((option) => option.value);

    if (values.includes("all")) {
      setSelectedWorkers([]);
    } else {
      setSelectedWorkers(values);
    }
  };

  const openMapModal = (location, title) => {
    setMapLocation(location);
    setMapTitle(title);
    setShowMapModal(true);
  };

  const closeMapModal = () => {
    setShowMapModal(false);
    setMapLocation(null);
    setMapTitle("");
  };

  return (
    <div id="attendance_history_container">
      <div className="x_title">
        <BackButton to={`/${farmId}/operator`} label={props.t("Voltar")} />
        <h2>{props.t("Histórico de Ponto")}</h2>
        <div className="clearfix"></div>
      </div>

      {loading && workers.length === 0 ? (
        <div
          style={{ marginTop: "50px", marginLeft: "50%", minHeight: "200px" }}
        >
          <MDSpinner />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="history-filters">
            <div className="filter-row">
              <div className="form-group">
                <label htmlFor="start_date">{props.t("Data Início")}</label>
                <DateTime
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  id="start_date"
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">{props.t("Data Fim")}</label>
                <DateTime
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  id="end_date"
                />
              </div>
              <div className="form-group">
                <label htmlFor="worker_filter">
                  {props.t("Selecionar Trabalhadores")}
                </label>
                <select
                  multiple
                  className="form-control"
                  value={selectedWorkers}
                  onChange={handleWorkerSelection}
                  id="worker_filter"
                  style={{ height: "120px" }}
                >
                  <option value="all">
                    {props.t("Todos os Trabalhadores")}
                  </option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.operator_name.trim()}
                    </option>
                  ))}
                </select>
                <small
                  style={{ display: "block", marginTop: "5px", color: "#666" }}
                >
                  {props.t("Segure Ctrl para selecionar múltiplos")}
                </small>
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <button
                  className="btn btn-primary"
                  onClick={fetchAttendanceHistory}
                  style={{ width: "100%" }}
                >
                  {props.t("Filtrar")}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                marginTop: "50px",
                marginLeft: "50%",
                minHeight: "200px",
              }}
            >
              <MDSpinner />
            </div>
          ) : (
            <>
              {/* Charts Section */}
              {chartData.length > 0 && (
                <div className="charts-section">
                  <div className="chart-container">
                    <h3>{props.t("Horas Trabalhadas por Dia")}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                          label={{
                            value: "Horas",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                          dataKey="hours"
                          fill="#007bff"
                          name={props.t("Horas")}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-container">
                    <h3>{props.t("Tendência de Frequência Semanal")}</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis
                          yAxisId="left"
                          label={{
                            value: "Trabalhadores",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          label={{
                            value: "Horas Totais",
                            angle: 90,
                            position: "insideRight",
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="workers"
                          stroke="#28a745"
                          name={props.t("Trabalhadores")}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="totalHours"
                          stroke="#ffc107"
                          name={props.t("Horas Totais")}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Records Table */}
              <div className="history-table-container">
                <h3>{props.t("Registros de Ponto")}</h3>
                <table className="table table-hover table-striped">
                  <thead>
                    <tr>
                      <th>{props.t("Data")}</th>
                      <th>{props.t("Trabalhador")}</th>
                      <th>{props.t("Hora de Entrada")}</th>
                      <th>{props.t("Hora de Saída")}</th>
                      <th>{props.t("Total de Horas")}</th>
                      <th>{props.t("Localização de Entrada")}</th>
                      <th>{props.t("Localização de Saída")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRecords.length === 0 ? (
                      <tr>
                        <td colSpan="7" align="center">
                          <h4>
                            {props.t(
                              "Nenhum registro encontrado para o período selecionado"
                            )}
                          </h4>
                        </td>
                      </tr>
                    ) : (
                      groupedRecords.map((record, index) => {
                        const worker = workers.find(
                          (w) => w.id === record.workerId
                        );
                        return (
                          <tr key={index}>
                            <td>
                              {format(parseISO(record.date), "dd/MM/yyyy")}
                            </td>
                            <td>{worker ? `${worker.operator_name}` : "-"}</td>
                            <td>
                              {record.clockIn
                                ? format(new Date(record.clockIn), "HH:mm:ss")
                                : "-"}
                            </td>
                            <td>
                              {record.clockOut
                                ? format(new Date(record.clockOut), "HH:mm:ss")
                                : "-"}
                            </td>
                            <td>
                              {calculateTotalHours(
                                record.clockIn,
                                record.clockOut
                              )}
                            </td>
                            <td>
                              {record.clockInLocation ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-link"
                                    onClick={() =>
                                      openMapModal(
                                        record.clockInLocation,
                                        `Entrada - ${
                                          worker ? worker.operator_name : ""
                                        } - ${format(
                                          parseISO(record.date),
                                          "dd/MM/yyyy"
                                        )}`
                                      )
                                    }
                                    style={{
                                      padding: "0",
                                      marginRight: "10px",
                                    }}
                                  >
                                    Ver no mapa
                                  </button>
                                  <a
                                    href={`https://www.google.com/maps?q=${record.clockInLocation.lat},${record.clockInLocation.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#007bff",
                                      fontSize: "12px",
                                    }}
                                  >
                                    (Google Maps)
                                  </a>
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {record.clockOutLocation ? (
                                <>
                                  <button
                                    className="btn btn-sm btn-link"
                                    onClick={() =>
                                      openMapModal(
                                        record.clockOutLocation,
                                        `Saída - ${
                                          worker ? worker.operator_name : ""
                                        } - ${format(
                                          parseISO(record.date),
                                          "dd/MM/yyyy"
                                        )}`
                                      )
                                    }
                                    style={{
                                      padding: "0",
                                      marginRight: "10px",
                                    }}
                                  >
                                    Ver no mapa
                                  </button>
                                  <a
                                    href={`https://www.google.com/maps?q=${record.clockOutLocation.lat},${record.clockOutLocation.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#007bff",
                                      fontSize: "12px",
                                    }}
                                  >
                                    (Google Maps)
                                  </a>
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Export Button */}
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <ExportButton
                  classname="btn btn-default-export"
                  export={`${
                    myConfig.BE_URL
                  }/api/attendance/export/?farm_id=${farmId}&from_date=${format(
                    startDate,
                    "yyyy-MM-dd"
                  )}&to_date=${format(endDate, "yyyy-MM-dd")}`}
                />
              </div>
            </>
          )}
        </>
      )}

      <ToastContainer />

      {/* Map Modal */}
      {showMapModal && mapLocation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Overlay */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            }}
            onClick={closeMapModal}
          />

          {/* Modal Content */}
          <div
            style={{
              position: "relative",
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "900px",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
                borderBottom: "1px solid #e0e0e0",
                paddingBottom: "10px",
              }}
            >
              <h3 style={{ margin: 0 }}>{mapTitle}</h3>
              <button
                onClick={closeMapModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>

            {/* Map */}
            <div style={{ height: "500px", width: "100%" }}>
              <PolygonMap
                map={{
                  centroidLat: mapLocation.lat,
                  centroidLng: mapLocation.lng,
                  lines: "",
                  stops: [],
                  landplots: [],
                }}
                map_salt={Date.now()}
                t={props.t}
              />
            </div>

            {/* Footer */}
            <div style={{ marginTop: "15px", textAlign: "center" }}>
              <button className="btn btn-secondary" onClick={closeMapModal}>
                {props.t("Fechar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default withTranslation()(AttendanceHistory);
